import { useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import EditablePageHeader from '../components/EditablePageHeader';
import { apiRequest, tryApiRequest, getAuthUser, getAuthToken, API_BASE } from '../lib/api';
import { wchIspReadUID, wchIspFlash } from '../lib/wch';
import { computeMd5 } from '../lib/md5';
import { showToast } from '../components/Toast';
import { CardSkeletonLoader } from '../components/SkeletonLoader';
import DeviceHistoryModal from '../components/DeviceHistoryModal';
import { formatVnDate } from '../lib/dateUtils';

let cachedFirmwares: FirmwareOption[] | null = null;

// ─── Types ────────────────────────────────────────────────────────────────────
type DeviceStatus = 'idle' | 'checking' | 'not_found' | 'unactivated' | 'owned_by_other' | 'owned';
type FlashStep = 'select_port' | 'connecting' | 'reading_uid' | 'uid_found' | 'downloading' | 'flashing' | 'done' | 'error';

interface DeviceInfo {
  id: string; deviceUid: string; model: string; variant?: string | null; source?: string; warrantyMonths?: number; createdAt?: string;
  daysUntilAutoActivate?: number | null;
}
interface WarrantyInfo {
  expiresAt: string; status: string; activatedAt: string;
  activationReason?: string;
  activationNote?: string | null;
}
interface FirmwareOption {
  id: string; version: string; bmcuModel: string; printerModel: string;
  amsSlot?: string | null; variant?: string | null;
  retractLen: string | null;
  fileUrl: string | null;
  md5?: string | null;
  checksum?: string | null;
  sizeBytes?: number | null;
  releaseNote: string | null;
}

// ─── Wizard Options ────────────────────────────────────────────────────────
// UI is now fully dynamic based on Admin Settings from the backend.
// ─── Firmware Wizard Component ─────────────────────────────────────────────────
function FirmwareWizard({ deviceId, deviceUid, onClose }: { deviceId: string; deviceUid: string; onClose: () => void }) {
  const [printerMode, setPrinterMode] = useState('');
  const [amsSlot, setAmsSlot] = useState('');
  const [retractLen, setRetractLen] = useState('');
  const [settings, setSettings] = useState<{ printer_models: string[], slots: string[], retract_lengths: string[] }>({ printer_models: [], slots: [], retract_lengths: [] });

  const [matchedFws, setMatchedFws] = useState<FirmwareOption[]>([]);
  const [selectedFw, setSelectedFw] = useState<FirmwareOption | null>(null);
  const [allFw, setAllFw] = useState<FirmwareOption[]>([]);
  const [loadingFw, setLoadingFw] = useState(true);
  const [flashStep, setFlashStep] = useState<FlashStep>('select_port');
  const [flashLog, setFlashLog] = useState<string[]>([]);
  const [flashProgress, setFlashProgress] = useState(0);
  const [flashError, setFlashError] = useState('');
  const [serialPort, setSerialPort] = useState<SerialPort | null>(null);
  const [showLogReview, setShowLogReview] = useState(false);
  const logEndRef = useRef<HTMLDivElement>(null);

  const addLog = (msg: string) => {
    setFlashLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
    setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  useEffect(() => {
    (async () => {
      const res = await tryApiRequest<{ firmware: FirmwareOption[], settings: Record<string, string[]> }>('/firmware/list');
      setAllFw(res?.firmware ?? []);

      if (res?.settings) {
        const s = res.settings;
        const p = s.printer_models || ['A1/A1 Mini (Standard)', 'A1/A1 Mini (Soft Load)', 'P1S (High Force Load)'];
        const v = s.slots || ['AMS_A', 'AMS_B', 'AMS_C', 'AMS_D'];
        const r = s.retract_lengths || ['0.10', '0.20', '0.25', '0.30', '0.35', '0.40', '0.45', '0.50', '0.55', '0.60', '0.65', '0.70', '0.75', '0.80', '0.85', '0.90'];
        setSettings({ printer_models: p, slots: v, retract_lengths: r });

        if (p.length > 0) setPrinterMode(p[0]);
        if (v.length > 0) setAmsSlot(v[0]);
        if (r.length > 0) {
          const defRetract = r.includes('0.10') ? '0.10' : r[0];
          setRetractLen(defRetract);
        }
      }
      setLoadingFw(false);
    })();
  }, []);

  // Set default initial selection when settings update
  useEffect(() => {
    if (settings.printer_models.length > 0 && !printerMode) {
      setPrinterMode(settings.printer_models[0]);
    }
    if (settings.slots.length > 0 && !amsSlot) {
      setAmsSlot(settings.slots[0]);
    }
    if (settings.retract_lengths.length > 0 && !retractLen) {
      const defRetract = settings.retract_lengths.includes('0.10') ? '0.10' : settings.retract_lengths[0];
      setRetractLen(defRetract);
    }
  }, [settings]);

  // Auto-match firmware based on wizard selections
  useEffect(() => {
    if (!allFw.length || !printerMode) return;

    const normalizeSlot = (s?: string | null) => (s || '').replace(/[-_]/g, '').toUpperCase();
    const normalizeRetract = (r?: string | null) => (r || '').replace('m', '').trim();

    const matches = allFw.filter(fw => {
      const isSolo = amsSlot.toLowerCase() === 'solo';
      const printerOk = fw.printerModel === printerMode;
      const slot = fw.amsSlot || fw.variant;
      const variantOk = isSolo
        ? (!slot || slot.toUpperCase() === 'SOLO')
        : (normalizeSlot(slot) === normalizeSlot(amsSlot));
      const retractOk = isSolo ? true : normalizeRetract(fw.retractLen) === normalizeRetract(retractLen);
      return printerOk && variantOk && retractOk;
    });

    matches.sort((a, b) => b.version.localeCompare(a.version));
    setMatchedFws(matches);

    if (matches.length > 0) {
      if (!matches.find(m => m.id === selectedFw?.id)) {
        setSelectedFw(matches[0]);
      }
    } else {
      setSelectedFw(null);
    }
  }, [printerMode, amsSlot, retractLen, allFw]);

  async function handleConnectAndFlash() {
    if (!selectedFw?.fileUrl) { setFlashError('Firmware chưa có file .bin để nạp.'); return; }

    setFlashStep('connecting');
    setFlashLog([]);
    setFlashProgress(0);
    setFlashError('');

    let port: ReturnType<Serial['requestPort']> extends Promise<infer P> ? P : never;
    try {
      addLog('Yêu cầu truy cập cổng Serial (USB)...');
      port = await (navigator as any).serial.requestPort();
      setSerialPort(port);
    } catch {
      setFlashStep('select_port');
      setFlashError('Người dùng đã hủy chọn cổng hoặc trình duyệt không hỗ trợ Web Serial.');
      return;
    }

    setFlashStep('reading_uid');
    let uid: string | null = null;
    try {
      uid = await wchIspReadUID(port, () => { }); // silent UID read
    } catch (e: any) {
      addLog(`Lỗi thiết lập: ${e.message}`);
    }

    if (uid) {
      setFlashStep('uid_found');
    } else {
      addLog('⚠️ Cảnh báo: Không thể đọc UID từ chip, tiếp tục nạp...');
      setFlashStep('uid_found');
    }

    // Pre-flash validate
    let sessionToken = '';
    try {
      const validateResp = await apiRequest<{ ok: boolean; flashSessionToken: string }>(
        '/firmware/preflash-validate',
        { method: 'POST', body: JSON.stringify({ deviceUid: uid || deviceUid, firmwareId: selectedFw.id }) },
        true
      );
      sessionToken = validateResp.flashSessionToken;
    } catch (e: any) {
      setFlashError(`Xác thực thất bại: ${e.message}`);
      setFlashStep('error');
      return;
    }

    // Download firmware
    setFlashStep('downloading');
    addLog(`Tải firmware v${selectedFw.version}...`);
    let fwData: Uint8Array;
    try {
      const fwUrl = selectedFw.fileUrl!.startsWith('http') ? selectedFw.fileUrl! : `${API_BASE.replace('/api', '')}${selectedFw.fileUrl}`;
      const resp = await fetch(fwUrl, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const buf = await resp.arrayBuffer();
      fwData = new Uint8Array(buf);

      // Kiểm tra dung lượng file nếu server có khai báo
      if (selectedFw.sizeBytes && selectedFw.sizeBytes > 0 && selectedFw.sizeBytes !== fwData.length) {
        setFlashError('Dữ liệu firmware tải về không trọn vẹn. Vui lòng thử lại.');
        setFlashStep('error');
        return;
      }

      // Xác minh toàn vẹn bằng MD5
      if (selectedFw.md5) {
        const calculatedMd5 = computeMd5(fwData);
        if (calculatedMd5.toLowerCase() !== selectedFw.md5.toLowerCase().trim()) {
          setFlashError('Gói dữ liệu firmware bị lỗi trong quá trình truyền mạng. Đã hủy nạp để bảo vệ thiết bị!');
          setFlashStep('error');
          return;
        }
      } else if (selectedFw.checksum) {
        const hashBuf = await crypto.subtle.digest('SHA-256', fwData.buffer as ArrayBuffer);
        const hash = Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, '0')).join('');
        if (hash.toLowerCase() !== selectedFw.checksum.toLowerCase().trim()) {
          setFlashError('Gói dữ liệu firmware không hợp lệ. Vui lòng thử lại!');
          setFlashStep('error');
          return;
        }
      }
      addLog('✅ Xác thực firmware thành công.');
    } catch (e: any) {
      setFlashError(`Không tải được firmware: ${e.message}`);
      setFlashStep('error');
      return;
    }

    // Flash!
    setFlashStep('flashing');
    addLog('Bắt đầu nạp firmware vào BMCU...');
    let flashOk = false;
    try {
      flashOk = await wchIspFlash(port, fwData, addLog, setFlashProgress);
    } catch (e: any) {
      addLog(`Lỗi nạp: ${e.message}`);
      flashOk = false;
    }

    // Report result
    try {
      await apiRequest('/firmware/flash-complete', {
        method: 'POST',
        body: JSON.stringify({ 
          flashSessionToken: sessionToken, 
          firmwareId: selectedFw.id, 
          result: flashOk ? 'success' : 'failed',
          checksum: selectedFw.md5 || selectedFw.checksum || undefined
        })
      }, true);
    } catch { /* non-critical */ }

    if (flashOk) {
      setFlashStep('done');
      addLog('🎉 Nạp firmware thành công!');
    } else {
      setFlashStep('error');
      setFlashError('Quá trình nạp thất bại. Vui lòng thử lại.');
    }
  }

  const sCard: React.CSSProperties = {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px',
    padding: '18px 20px', cursor: 'pointer', transition: 'all 0.2s', marginBottom: '10px', color: '#fff'
  };
  const sCardActive: React.CSSProperties = {
    ...sCard, background: 'rgba(225, 29, 72, 0.12)', border: '1px solid #e11d48',
  };

  const isFlashing = ['connecting', 'reading_uid', 'uid_found', 'downloading', 'flashing'].includes(flashStep);

  useEffect(() => {
    if (!isFlashing) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Đang nạp firmware cho bo mạch BMCU. Không được đóng hoặc tải lại trang!';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isFlashing]);

  return (
    <div 
      className={`modal-overlay ${isFlashing ? 'is-locked' : ''}`} 
      onClick={isFlashing ? (e) => {
        e.stopPropagation();
        showToast('⚠️ Đang trong quá trình nạp firmware, cửa sổ đã được khóa tự động để bảo vệ thiết bị!', 'warning');
      } : onClose}
    >
      <div className="modal-content" style={{ maxWidth: '760px', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <button 
          className="modal-close" 
          onClick={isFlashing ? undefined : onClose}
          disabled={isFlashing}
          title={isFlashing ? "Đang nạp firmware, không thể đóng popup" : "Đóng"}
        >
          {isFlashing ? '🔒' : '×'}
        </button>
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '16px', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, color: '#fff', fontSize: '1.4rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>🔧</span>
            <span>Nạp Firmware cho BMCU</span>
          </h2>
          <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '4px' }}>
            Cấu hình thông số thiết bị và nạp phần mềm trực tiếp qua USB Type-C
          </div>
        </div>

        {flashStep === 'done' ? (
          <div style={{ textAlign: 'center', padding: '10px 0 16px' }}>
            {/* Hologram celebration ring */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(74, 222, 128, 0.25) 0%, rgba(74, 222, 128, 0.05) 70%)',
              border: '2px solid #4ade80',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 0 30px rgba(74, 222, 128, 0.4), inset 0 0 15px rgba(74, 222, 128, 0.2)'
            }}>
              <span style={{ fontSize: '36px' }}>⚡</span>
            </div>

            <h3 style={{ fontSize: '1.5rem', color: '#4ade80', margin: '0 0 6px', fontWeight: 800 }}>
              Nạp Firmware Thành Công!
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 20px' }}>
              Bo mạch BMCU đã được nạp hoàn tất bản <strong style={{ color: '#fff' }}>v{selectedFw?.version}</strong> và tự động khởi động vào chế độ làm việc.
            </p>

            {/* Hardware Specification Certificate Card */}
            {selectedFw && (
              <div className="bmcu-handover-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(74, 222, 128, 0.25)', paddingBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>🛡️</span>
                    <strong style={{ color: '#a7f3d0', fontSize: '13px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                      Cấu hình đã nạp cho BMCU
                    </strong>
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: '11px', background: 'rgba(74, 222, 128, 0.2)', border: '1px solid #4ade80', color: '#86efac', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                    VERIFIED ✅
                  </span>
                </div>

                <div className="bmcu-handover-spec-grid">
                  <div className="bmcu-handover-spec-item">
                    <span className="bmcu-handover-spec-label">Mẫu BMCU</span>
                    <span className="bmcu-handover-spec-val">{selectedFw.bmcuModel}</span>
                  </div>
                  <div className="bmcu-handover-spec-item">
                    <span className="bmcu-handover-spec-label">Chế độ nạp</span>
                    <span className="bmcu-handover-spec-val">{selectedFw.printerModel}</span>
                  </div>
                  <div className="bmcu-handover-spec-item">
                    <span className="bmcu-handover-spec-label">AMS Slot</span>
                    <span className="bmcu-handover-spec-val" style={{ color: '#fb7185' }}>
                      {selectedFw.amsSlot || selectedFw.variant || 'SOLO'}
                    </span>
                  </div>
                  {selectedFw.retractLen && (
                    <div className="bmcu-handover-spec-item">
                      <span className="bmcu-handover-spec-label">Độ lùi sợi</span>
                      <span className="bmcu-handover-spec-val" style={{ color: '#fbbf24' }}>{selectedFw.retractLen}m</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 3 Crucial Next Steps Checklist */}
            <div className="bmcu-next-steps-box">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ fontWeight: 700, color: '#f8fafc', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📋</span> <span>Hướng dẫn 3 bước tiếp theo (Chuẩn kỹ thuật):</span>
                </div>
                <a 
                  href="/news/huong-dan-cai-at-va-su-dung-bmcu-370-type-c" 
                  target="_blank" 
                  rel="noreferrer"
                  style={{ fontSize: '12.5px', color: '#fb7185', textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                  Xem bài viết hướng dẫn chi tiết →
                </a>
              </div>

              {/* Step 1 */}
              <div className="bmcu-next-step-row">
                <span className="bmcu-next-step-num">1</span>
                <div>
                  <strong style={{ color: '#fff' }}>Rút cáp USB & TẮT NGUỒN máy in 3D:</strong>{' '}
                  Rút cáp Type-C khỏi máy tính. Đảm bảo <strong style={{ color: '#fda4af' }}>máy in 3D đã tắt nguồn hoàn toàn</strong> trước khi cắm nối bo mạch. 
                  <span style={{ color: '#f87171', display: 'block', fontSize: '12px', marginTop: '3px' }}>
                    ⚠️ Tuyệt đối không cắm/rút nóng khi máy in đang mở để tránh sốc điện làm cháy chip!
                  </span>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bmcu-next-step-row">
                <span className="bmcu-next-step-num">2</span>
                <div>
                  <strong style={{ color: '#fff' }}>Cắm cáp kết nối & ĐỂ TRỐNG 4 KHE NHỰA:</strong>{' '}
                  Cắm ngập sâu cáp nối giữa bo mạch BMCU và máy in 3D. <strong style={{ color: '#fda4af' }}>Không đút bất kỳ sợi nhựa nào</strong> vào 4 cổng lúc này để hệ thống chuẩn bị hiệu chuẩn không tải.
                </div>
              </div>

              {/* Step 3 */}
              <div className="bmcu-next-step-row">
                <span className="bmcu-next-step-num">3</span>
                <div>
                  <strong style={{ color: '#fff' }}>Bật nguồn & Hiệu chuẩn (Calibration) theo đèn LED:</strong>{' '}
                  Bật công tắc máy in (đèn LED nháy Vàng 3 lần). Thao tác lần lượt trên từng khe theo tín hiệu đèn:
                  <div style={{ marginTop: '8px', fontSize: '12.5px', background: 'rgba(0,0,0,0.3)', padding: '10px 12px', borderRadius: '8px', borderLeft: '3px solid #e11d48', lineHeight: 1.6 }}>
                    <div style={{ marginBottom: '3px' }}>
                      🔹 <strong style={{ color: '#38bdf8' }}>Đèn nháy Xanh dương:</strong> Dùng tay đè đầu khí nén sát đáy trong 2 giây rồi thả.
                    </div>
                    <div style={{ marginBottom: '3px' }}>
                      🔻 <strong style={{ color: '#f87171' }}>Đèn nháy Đỏ:</strong> Dùng tay kéo đầu khí nén ra hết mức trong 2 giây rồi thả.
                    </div>
                    <div style={{ color: '#4ade80', fontSize: '12px', marginTop: '4px' }}>
                      ✨ Toàn bộ đèn LED chớp xanh lá là quá trình hiệu chuẩn hoàn tất thành công!
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Log Review Box (Collapsible) */}
            {showLogReview && (
              <div className="bmcu-neon-terminal" style={{ textAlign: 'left', marginBottom: '20px' }}>
                <div className="bmcu-terminal-header">
                  <div className="bmcu-terminal-dots">
                    <span className="bmcu-terminal-dot dot-red" />
                    <span className="bmcu-terminal-dot dot-yellow" />
                    <span className="bmcu-terminal-dot dot-green" />
                    <span className="bmcu-terminal-title" style={{ marginLeft: '6px' }}>
                      📜 Nhật ký nạp đã hoàn tất
                    </span>
                  </div>
                  <div className="bmcu-terminal-badge" style={{ color: '#4ade80', borderColor: '#4ade80', background: 'rgba(74, 222, 128, 0.15)' }}>
                    SUCCESS
                  </div>
                </div>
                <div className="bmcu-terminal-body" style={{ maxHeight: '180px' }}>
                  {flashLog.map((l, i) => (
                    <div key={i} style={{ color: l.includes('❌') ? '#fca5a5' : (l.includes('✅') || l.includes('🎉')) ? '#4ade80' : '#cbd5e1', marginBottom: '2px' }}>
                      {l}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button 
                className="button outline" 
                onClick={() => setShowLogReview(!showLogReview)} 
                style={{ minWidth: '150px', fontSize: '13px' }}
              >
                {showLogReview ? 'Ẩn nhật ký log' : '📜 Xem lại nhật ký'}
              </button>
              <button 
                className="button primary" 
                onClick={onClose} 
                style={{ minWidth: '180px', background: 'linear-gradient(135deg, #10b981, #059669)', borderColor: '#10b981', boxShadow: '0 0 15px rgba(16, 185, 129, 0.4)' }}
              >
                ✅ Hoàn tất & Đóng
              </button>
            </div>
          </div>
        ) : flashStep === 'error' ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(239, 68, 68, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 0 0 8px rgba(239, 68, 68, 0.1)' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </div>
            <h3 style={{ fontSize: '1.5rem', color: '#f87171', margin: '0 0 8px' }}>Nạp Firmware Thất Bại</h3>
            <p style={{ color: '#94a3b8', marginBottom: '24px', fontSize: '15px' }}>{flashError}</p>
            
            <div className="bmcu-neon-terminal terminal-error" style={{ textAlign: 'left', marginBottom: '24px' }}>
              <div className="bmcu-terminal-header header-error">
                <div className="bmcu-terminal-dots">
                  <span className="bmcu-terminal-dot dot-red" />
                  <span className="bmcu-terminal-dot dot-yellow" />
                  <span className="bmcu-terminal-dot dot-green" />
                  <span className="bmcu-terminal-title" style={{ marginLeft: '6px', color: '#fca5a5' }}>
                    ❌ Nhật Ký Quá Trình Nạp
                  </span>
                </div>
                <div className="bmcu-terminal-badge" style={{ background: 'rgba(239, 68, 68, 0.2)', borderColor: 'rgba(239, 68, 68, 0.4)', color: '#f87171' }}>
                  LỖI
                </div>
              </div>
              <div className="bmcu-terminal-body" style={{ maxHeight: '200px' }}>
                {flashLog.map((l, i) => (
                  <div key={i} style={{ color: l.includes('❌') ? '#fca5a5' : (l.includes('✅') || l.includes('🎉')) ? '#86efac' : '#cbd5e1', marginBottom: '2px' }}>
                    {l}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button className="button outline" onClick={onClose} style={{ minWidth: '120px' }}>Hủy bỏ</button>
              <button className="button primary" onClick={() => { setFlashStep('select_port'); setFlashError(''); }} style={{ minWidth: '160px', background: '#e11d48', borderColor: '#e11d48', color: '#fff' }}>🔄 Thử lại ngay</button>
            </div>
          </div>
        ) : isFlashing ? (
          <div>
            <div className="bmcu-neon-terminal">
              <div className="bmcu-terminal-header">
                <div className="bmcu-terminal-dots">
                  <span className="bmcu-terminal-dot dot-red" />
                  <span className="bmcu-terminal-dot dot-yellow" />
                  <span className="bmcu-terminal-dot dot-green" />
                  <span className="bmcu-terminal-title" style={{ marginLeft: '6px' }}>
                    ⚡ Terminal Nạp Firmware BMCU
                  </span>
                </div>
                <div className="bmcu-terminal-badge">
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f43f5e', display: 'inline-block', boxShadow: '0 0 6px #f43f5e' }} />
                  {flashStep === 'connecting' && 'KẾT NỐI COM'}
                  {flashStep === 'reading_uid' && 'ĐỌC CHIP UID'}
                  {flashStep === 'uid_found' && 'UID ĐÃ XÁC NHẬN'}
                  {flashStep === 'downloading' && 'TẢI FIRMWARE'}
                  {flashStep === 'flashing' && `GHI FLASH: ${flashProgress}%`}
                </div>
              </div>

              <div className="bmcu-terminal-body">
                {flashLog.map((l, i) => (
                  <div key={i} style={{ 
                    color: (l.includes('✅') || l.includes('🎉')) ? '#4ade80' : l.includes('❌') ? '#f87171' : l.includes('⚠️') ? '#fbbf24' : (l.includes('⚡') || l.includes('🚀') || l.includes('📦')) ? '#fda4af' : '#cbd5e1', 
                    marginBottom: '3px' 
                  }}>
                    {l}
                  </div>
                ))}
                <div ref={logEndRef} style={{ display: 'inline-block' }}>
                  <span className="bmcu-terminal-cursor" />
                </div>
              </div>
            </div>

            {flashStep === 'flashing' && (
              <div style={{ marginBottom: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="spinner" style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.2)', borderTopColor: '#f43f5e', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Tiến trình ghi bộ nhớ Flash
                  </span>
                  <span style={{ fontWeight: 800, color: '#fb7185', fontSize: '15px', textShadow: '0 0 10px rgba(225, 29, 72, 0.6)' }}>
                    {flashProgress}%
                  </span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '99px', height: '10px', overflow: 'hidden', padding: '1px', border: '1px solid rgba(225, 29, 72, 0.3)' }}>
                  <div style={{ 
                    width: `${flashProgress}%`, 
                    background: 'linear-gradient(90deg, #be123c 0%, #e11d48 50%, #fb7185 100%)', 
                    height: '100%', 
                    borderRadius: '99px',
                    boxShadow: '0 0 14px rgba(225, 29, 72, 0.8)',
                    transition: 'width 0.25s ease-out' 
                  }} />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#94a3b8', fontSize: '14px', marginBottom: '8px' }}>
              <div className="spinner" style={{ width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.2)', borderTopColor: '#f43f5e', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
              <span style={{ color: '#cbd5e1' }}>
                {flashStep === 'connecting' && 'Đang kết nối cổng Serial...'}
                {flashStep === 'reading_uid' && 'Đang đọc UID vi điều khiển...'}
                {flashStep === 'uid_found' && 'Đã xác nhận chip, chuẩn bị nạp...'}
                {flashStep === 'downloading' && 'Đang tải bản firmware từ máy chủ...'}
                {flashStep === 'flashing' && 'Đang truyền dữ liệu vào chip, vui lòng giữ nguyên kết nối...'}
              </span>
            </div>

            <div className="bmcu-flash-lock-banner">
              <span style={{ fontSize: '20px', flexShrink: 0 }}>🔒</span>
              <div>
                <strong style={{ color: '#fff' }}>Khóa an toàn đang bật:</strong> Popup đã được vô hiệu hóa đóng để bảo vệ chip vi điều khiển. Vui lòng không tắt trang web và <strong>tuyệt đối không rút cáp USB</strong> trong khi nạp!
              </div>
            </div>
          </div>
        ) : (
          <div>
            {/* Step 1: Printer Mode */}
            <div style={{ marginBottom: '24px' }}>
              <div className="bmcu-step-header">
                <span className="bmcu-step-badge">BƯỚC 01</span>
                <span className="bmcu-step-title">Chế độ nạp / Dòng máy in</span>
                <span className="bmcu-step-sub">Chọn cấu hình tương ứng máy in</span>
              </div>
              <div className="bmcu-tech-grid">
                {settings.printer_models.map(opt => {
                  const isActive = printerMode === opt;
                  const isP1S = opt.includes('P1S') || opt.includes('High');
                  const isSoft = opt.includes('Soft');
                  const icon = isP1S ? '⚡' : isSoft ? '🪶' : '🖨️';
                  const name = isP1S ? 'P1S / X1C' : 'A1 / A1 Mini';
                  const desc = isP1S 
                    ? 'High Force Load · Lực kéo khỏe, khử kẹt nhựa'
                    : isSoft 
                    ? 'Soft Load · Lực kéo nhẹ, êm ái tối đa'
                    : 'Standard Load · Lực kéo chuẩn, ổn định';

                  return (
                    <div 
                      key={opt} 
                      onClick={() => setPrinterMode(opt)}
                      className={`bmcu-tech-tile ${isActive ? 'active' : ''}`}
                    >
                      <div className="bmcu-tech-tile-top">
                        <span className="bmcu-tech-tile-icon">{icon}</span>
                        <span className="bmcu-tech-tile-led" />
                      </div>
                      <div className="bmcu-tech-tile-name">{name}</div>
                      <div className="bmcu-tech-tile-desc">{desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Step 2: AMS Slot */}
            <div style={{ marginBottom: '24px' }}>
              <div className="bmcu-step-header">
                <span className="bmcu-step-badge">BƯỚC 02</span>
                <span className="bmcu-step-title">AMS Slot</span>
                <span className="bmcu-step-sub">Nếu bạn có 1 BMCU thì chọn AMS_A</span>
              </div>
              <div className="bmcu-slot-grid">
                {settings.slots.map(opt => {
                  const isActive = amsSlot === opt;
                  const isSolo = opt.toUpperCase() === 'SOLO';
                  const label = isSolo ? 'SOLO' : opt;

                  return (
                    <div 
                      key={opt} 
                      onClick={() => setAmsSlot(opt)}
                      className={`bmcu-slot-bay ${isActive ? 'active' : ''}`}
                    >
                      <div className="bmcu-slot-bay-indicator" />
                      <div className="bmcu-slot-bay-label">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Step 3: Retract Length (only for AMS slots) */}
            {amsSlot.toLowerCase() !== 'solo' && (
              <div style={{ marginBottom: '26px' }}>
                <div className="bmcu-step-header">
                  <span className="bmcu-step-badge">BƯỚC 03</span>
                  <span className="bmcu-step-title">Độ lùi sợi (Retract Length)</span>
                  <span className="bmcu-step-sub">Khoảng cách rút sợi tính bằng mét</span>
                </div>
                <div className="bmcu-retract-flex">
                  {settings.retract_lengths.map(r => (
                    <button 
                      key={r} 
                      onClick={() => setRetractLen(r)}
                      className={`bmcu-retract-pill ${retractLen === r ? 'active' : ''}`}
                    >
                      {r}m
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Matched Firmware Preview */}
            <div style={{ 
              background: loadingFw ? 'rgba(255,255,255,0.02)' : matchedFws.length > 0 ? 'radial-gradient(circle at 10% 10%, rgba(225, 29, 72, 0.08) 0%, rgba(18, 10, 14, 0.95) 100%)' : 'rgba(239, 68, 68, 0.06)', 
              border: `1px solid ${matchedFws.length > 0 ? 'rgba(225, 29, 72, 0.35)' : 'rgba(239, 68, 68, 0.2)'}`, 
              borderRadius: '16px', 
              padding: '20px', 
              marginBottom: '24px',
              boxShadow: matchedFws.length > 0 ? '0 0 20px rgba(225, 29, 72, 0.18)' : 'none'
            }}>
              {loadingFw ? (
                <p style={{ margin: 0, color: '#94a3b8' }}>Đang tải danh sách firmware tương thích...</p>
              ) : matchedFws.length > 0 ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>⚡</span>
                      <h4 style={{ margin: 0, fontSize: '15px', color: '#fb7185', fontWeight: 700 }}>
                        Bản Firmware Tương Thích Nhất
                      </h4>
                    </div>
                    <span style={{ fontFamily: 'monospace', fontSize: '11px', background: 'rgba(225, 29, 72, 0.15)', border: '1px solid #e11d48', color: '#fda4af', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                      KHỚP 100% CẤU HÌNH
                    </span>
                  </div>

                  <div style={{ display: 'grid', gap: '10px' }}>
                    {matchedFws.map(fw => {
                      const isSelected = selectedFw?.id === fw.id;
                      return (
                        <div 
                          key={fw.id} 
                          onClick={() => setSelectedFw(fw)} 
                          style={{
                            padding: '14px 18px', 
                            borderRadius: '12px', 
                            background: isSelected 
                              ? 'radial-gradient(circle at 10% 10%, rgba(225, 29, 72, 0.16) 0%, rgba(20, 8, 12, 0.95) 100%)' 
                              : 'rgba(255,255,255,0.02)', 
                            border: '1.5px solid',
                            borderColor: isSelected ? '#e11d48' : 'rgba(255,255,255,0.08)', 
                            cursor: 'pointer',
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            gap: '14px', 
                            transition: 'all 0.2s ease',
                            boxShadow: isSelected 
                              ? '0 0 18px rgba(225, 29, 72, 0.4), inset 0 0 12px rgba(225, 29, 72, 0.08)' 
                              : 'none'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 800, fontSize: '16px', color: isSelected ? '#fff' : '#f1f5f9', letterSpacing: '-0.3px' }}>v{fw.version}</span>
                              <span style={{ fontSize: '12px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(34, 197, 94, 0.15)', color: '#4ade80', fontWeight: 600 }}>CHÍNH THỨC</span>
                              <span style={{ fontSize: '13px', color: '#94a3b8' }}>· {fw.bmcuModel} · {fw.printerModel}{(fw.amsSlot || fw.variant) && ` · ${fw.amsSlot || fw.variant}`}</span>
                            </div>
                            {fw.releaseNote && (
                              <div style={{ 
                                marginTop: '10px', 
                                fontSize: '13px', 
                                color: '#e2e8f0', 
                                lineHeight: 1.55, 
                                background: 'rgba(0, 0, 0, 0.28)', 
                                border: '1px solid rgba(255, 255, 255, 0.08)', 
                                padding: '10px 14px', 
                                borderRadius: '8px'
                              }}>
                                {fw.releaseNote}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {selectedFw && !selectedFw.fileUrl && (
                    <div style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#fcd34d', marginTop: '12px' }}>
                      ⚠️ Firmware này chưa có file .bin để nạp.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>❌</span>
                  <p style={{ margin: 0, color: '#f87171', fontWeight: 600 }}>Không tìm thấy firmware phù hợp với lựa chọn của bạn.</p>
                </div>
              )}
            </div>

            {flashError && (
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#f87171', fontSize: '14px' }}>
                {flashError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', alignItems: 'center', marginTop: '10px' }}>
              <button className="button outline" onClick={onClose} style={{ minWidth: '100px', borderRadius: '10px' }}>Huỷ</button>
              {!(navigator as any).serial ? (
                <div style={{ padding: '8px 16px', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '10px', fontSize: '13px', color: '#fcd34d' }}>
                  ⚠️ Trình duyệt chưa hỗ trợ Web Serial (dùng Chrome/Edge)
                </div>
              ) : (
                <button
                  className="button primary"
                  onClick={handleConnectAndFlash}
                  disabled={!selectedFw?.fileUrl}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '10px',
                    background: selectedFw?.fileUrl ? 'linear-gradient(135deg, #e11d48, #be123c)' : undefined,
                    borderColor: '#e11d48',
                    boxShadow: selectedFw?.fileUrl ? '0 0 20px rgba(225, 29, 72, 0.5)' : 'none',
                    fontWeight: 700,
                    fontSize: '15px'
                  }}
                >
                  ⚡ Kết nối & Nạp Firmware Ngay
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── UID Check / Activation Panel ─────────────────────────────────────────────
function ActivateDevicePanel({ onActivated, showToast }: { onActivated: () => void, showToast: (m: string, t: 'info' | 'error' | 'warning') => void }) {
  const [checking, setChecking] = useState(false);
  const [readingSerial, setReadingSerial] = useState(false);
  const [status, setStatus] = useState<{ status: string; message?: string; device?: any; warranty?: any; daysUntilAutoActivate?: number | null } | null>(null);
  const [rawUid, setRawUid] = useState<string | null>(null);

  async function handleConnectAndRead() {
    setReadingSerial(true);
    setStatus(null);
    setRawUid(null);

    try {
      const port = await (navigator as any).serial.requestPort();
      const uid = await wchIspReadUID(port, console.log);

      if (uid === "ERROR_PROTECTED") {
        showToast('Mạch đang bị khóa Read Protect. Hãy nạp firmware để mở khóa!', 'warning');
        setReadingSerial(false);
        return;
      }

      if (!uid) {
        showToast('Không thể đọc UID. Có thể firmware chưa hỗ trợ lệnh đọc UID qua USB.', 'error');
        setReadingSerial(false);
        return;
      }

      setRawUid(uid);

      // Now check backend
      setChecking(true);
      const res = await apiRequest<any>('/device/check-uid', { method: 'POST', body: JSON.stringify({ deviceUid: uid }) }, true);
      setStatus(res);
      setChecking(false);
      setReadingSerial(false);
    } catch (e: any) {
      showToast(`Lỗi kết nối USB: ${e.message}`, 'error');
      setReadingSerial(false);
      setChecking(false);
    }
  }

  async function handleActivate() {
    if (!status?.device) return;
    setChecking(true);
    try {
      await apiRequest('/device/activate', {
        method: 'POST',
        body: JSON.stringify({ deviceUid: status.device.deviceUid, model: status.device.model || 'BMCU-v2', source: status.device.source || 'SHOP' })
      }, true);
      onActivated();
    } catch (e: any) {
      setStatus({ status: 'error', message: e.message });
      showToast(e.message, 'error');
    } finally {
      setChecking(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount: 0.15 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(16px)', borderRadius: '24px', padding: '36px', maxWidth: '540px', margin: '0 auto', boxShadow: '0 20px 40px -15px rgba(0,0,0,0.5)' }}
    >
      <h3 style={{ margin: '0 0 8px', fontSize: '1.4rem', color: '#fff', fontWeight: 800 }}>🔑 Kích hoạt BMCU</h3>
      <p style={{ margin: '0 0 24px', color: '#94a3b8', fontSize: '14px', lineHeight: 1.6 }}>
        Kết nối bo mạch BMCU của bạn qua cổng USB Type-C để hệ thống tự động đọc mã UID và tiến hành kích hoạt bảo hành.
      </p>

      {!(navigator as any).serial ? (
        <div style={{ padding: '16px', background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '12px', fontSize: '14px', color: '#fcd34d', marginBottom: '20px', lineHeight: 1.5 }}>
          ⚠️ Trình duyệt của bạn chưa hỗ trợ Web Serial API. Vui lòng sử dụng <strong>Google Chrome</strong> hoặc <strong>Microsoft Edge</strong> trên máy tính.
        </div>
      ) : (
        <button className="button primary" style={{ width: '100%', marginBottom: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '14px' }} onClick={handleConnectAndRead} disabled={readingSerial || checking}>
          {readingSerial ? <div className="spinner" style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : '🔌'}
          {readingSerial ? 'Đang đọc UID từ bo mạch...' : checking ? 'Đang kiểm tra dữ liệu...' : 'Kết nối BMCU qua USB'}
        </button>
      )}

      {status && (
        <div style={{ marginTop: '20px', borderRadius: '14px', padding: '20px', background: status.status === 'owned' ? 'rgba(34, 197, 94, 0.08)' : status.status === 'unactivated' ? 'rgba(245, 158, 11, 0.08)' : 'rgba(239, 68, 68, 0.08)', border: `1px solid ${status.status === 'owned' ? 'rgba(34, 197, 94, 0.3)' : status.status === 'unactivated' ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)'}` }}>
          {status.status === 'not_found' && (
            <>
              <p style={{ margin: 0, color: '#f87171', fontWeight: 600 }}>❌ UID: {rawUid}</p>
              <p style={{ margin: '4px 0 0', color: '#f87171', fontSize: '14px' }}>Thiết bị không có trong hệ thống hoặc không do shop phân phối. Vui lòng liên hệ hỗ trợ.</p>
            </>
          )}

          {status.status === 'owned_by_other' && (
            <>
              <p style={{ margin: 0, color: '#fbbf24', fontWeight: 600 }}>⚠️ UID: {rawUid}</p>
              <p style={{ margin: '4px 0 0', color: '#fbbf24', fontSize: '14px' }}>Thiết bị này đã được đăng ký bởi tài khoản khác.</p>
            </>
          )}

          {status.status === 'error' && (
            <p style={{ margin: 0, color: '#f87171', fontWeight: 600 }}>❌ {status.message}</p>
          )}

          {status.status === 'owned' && (
            <>
              <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#4ade80' }}>✅ Thiết bị của bạn đã được kích hoạt!</p>
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '14px', fontSize: '13px', color: '#cbd5e1' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#94a3b8' }}>UID:</span> <strong style={{ color: '#fff' }}>{status.device?.deviceUid}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#94a3b8' }}>Model:</span> <strong style={{ color: '#fff' }}>{status.device?.model}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8' }}>Ngày kích hoạt:</span> <strong style={{ color: '#fff' }}>{status.warranty?.activatedAt ? formatVnDate(status.warranty.activatedAt) : 'N/A'}</strong>
                </div>
              </div>
            </>
          )}

          {status.status === 'unactivated' && (
            <>
              {status.warranty ? (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>{status.device?.source === 'EXTERNAL_APPROVED' ? '✅' : '🛡️'}</span>
                    <strong style={{ fontSize: '15px', color: '#38bdf8' }}>
                      {status.device?.source === 'EXTERNAL_APPROVED'
                        ? 'Thiết bị ngoài Shop (External) · Đã liên kết tài khoản'
                        : 'Thiết bị chính hãng 3D Smart · Bảo hành điện tử đã kích hoạt'}
                    </strong>
                  </div>
                  <div style={{ background: 'rgba(14, 165, 233, 0.08)', border: '1px solid rgba(14, 165, 233, 0.25)', borderRadius: '12px', padding: '14px', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.6' }}>
                    <p style={{ margin: '0 0 8px' }}>
                      {status.warranty.activationReason === 'AUTO_10_DAYS' ? (
                        <>
                          Theo chính sách bảo hành của 3D Smart, do sản phẩm đã xuất kho quá 10 ngày mà chưa kích hoạt thủ công, hệ thống đã <strong>tự động kích hoạt bảo hành điện tử</strong> (thời gian bảo hành được tính từ ngày xuất kho + 3 ngày giao hàng: <strong style={{ color: '#fff' }}>{formatVnDate(status.warranty.activatedAt)}</strong>).
                        </>
                      ) : (
                        <>
                          Sản phẩm đã được kích hoạt từ ngày <strong style={{ color: '#fff' }}>{formatVnDate(status.warranty.activatedAt)}</strong>.
                        </>
                      )}
                    </p>
                    <p style={{ margin: 0 }}>
                      {status.device?.source === 'EXTERNAL_APPROVED' ? (
                        'Hãy nhấn nút bên dưới để thêm thiết bị vào tài khoản của bạn để quản lý và cập nhật firmware trọn đời.'
                      ) : (
                        <>
                          Thời hạn bảo hành còn lại đến: <strong style={{ color: '#4ade80' }}>{formatVnDate(status.warranty.expiresAt)}</strong>.
                          Hãy nhấn nút bên dưới để <strong>thêm thiết bị vào tài khoản của bạn</strong> để nhận quyền sở hữu, quản lý bảo hành và cập nhật firmware.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: '20px' }}>
                  {status.device?.source === 'EXTERNAL_APPROVED' ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '20px' }}>✅</span>
                        <strong style={{ fontSize: '15px', color: '#38bdf8' }}>
                          Thiết bị ngoài Shop (External Approved)
                        </strong>
                      </div>
                      <div style={{ background: 'rgba(14, 165, 233, 0.08)', border: '1px solid rgba(14, 165, 233, 0.25)', borderRadius: '12px', padding: '14px', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.6' }}>
                        <p style={{ margin: 0 }}>
                          Thiết bị ngoài shop đã được phê duyệt hợp lệ trên hệ thống 3D Smart. Sản phẩm này <strong>không áp dụng gói bảo hành điện tử từ Shop</strong>. Khi kích hoạt hôm nay, thiết bị sẽ được liên kết chính thức vào tài khoản của bạn để quản lý và nạp firmware trọn đời.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '20px' }}>{status.device?.warrantyMonths === 0 ? '📦' : '🎉'}</span>
                        <strong style={{ fontSize: '15px', color: status.device?.warrantyMonths === 0 ? '#38bdf8' : '#4ade80' }}>
                          {status.device?.warrantyMonths === 0 ? 'Thiết bị chính hãng 3D Smart' : 'Thiết bị chính hãng 3D Smart mới 100%'}
                        </strong>
                      </div>
                      <div style={{ background: status.device?.warrantyMonths === 0 ? 'rgba(14, 165, 233, 0.08)' : 'rgba(34, 197, 94, 0.08)', border: `1px solid ${status.device?.warrantyMonths === 0 ? 'rgba(14, 165, 233, 0.25)' : 'rgba(34, 197, 94, 0.25)'}`, borderRadius: '12px', padding: '14px', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.6' }}>
                        {status.device?.warrantyMonths === 0 ? (
                          <p style={{ margin: 0 }}>
                            Thiết bị mới chưa qua sử dụng. Sản phẩm này <strong>không áp dụng gói bảo hành điện tử</strong>. Khi kích hoạt hôm nay, thiết bị sẽ được liên kết chính thức vào tài khoản của bạn để quản lý và nạp firmware trọn đời.
                          </p>
                        ) : (
                          <>
                            <p style={{ margin: '0 0 8px' }}>
                              Thiết bị chưa qua sử dụng. Khi bạn kích hoạt hôm nay, thời hạn bảo hành <strong style={{ color: '#fff' }}>{status.device?.warrantyMonths ?? 12} tháng</strong> sẽ được tính bắt đầu từ <strong>chính ngày hôm nay</strong>.
                            </p>
                            {status.daysUntilAutoActivate !== undefined && status.daysUntilAutoActivate !== null && status.daysUntilAutoActivate > 0 && (
                              <p style={{ margin: 0, color: '#facc15', fontSize: '12px' }}>
                                ⏳ <em>Lưu ý chính sách: Nếu bạn chưa kích hoạt thủ công, hệ thống sẽ tự động kích hoạt sau {status.daysUntilAutoActivate} ngày nữa kể từ ngày xuất kho (khi đó thời gian bảo hành sẽ được tính từ ngày xuất kho + 3 ngày giao hàng).</em>
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '16px', fontSize: '14px', color: '#cbd5e1', border: '1px solid rgba(255, 255, 255, 0.08)', marginBottom: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '8px', marginBottom: '8px' }}>
                  <span style={{ color: '#94a3b8' }}>UID Thiết Bị:</span>
                  <strong style={{ fontFamily: 'monospace', letterSpacing: '1px', color: '#fff' }}>{status.device?.deviceUid}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '8px', marginBottom: '8px' }}>
                  <span style={{ color: '#94a3b8' }}>Nguồn gốc:</span>
                  <strong style={{ color: status.device?.source === 'EXTERNAL_APPROVED' ? '#38bdf8' : '#4ade80' }}>
                    {status.device?.source === 'EXTERNAL_APPROVED' ? '✅ Ngoài Shop (External Approved)' : '🏪 Shop 3D Smart (Chính hãng)'}
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '8px', marginBottom: '8px' }}>
                  <span style={{ color: '#94a3b8' }}>Model:</span>
                  <strong style={{ color: '#fff' }}>{status.device?.model} {status.device?.variant ? `(${status.device?.variant})` : ''}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8' }}>Thời hạn bảo hành:</span>
                  <strong style={{ color: '#fff' }}>{status.device?.warrantyMonths === 0 ? 'Không bảo hành' : `${status.device?.warrantyMonths} tháng`}</strong>
                </div>
              </div>

              <button className="button primary" style={{ width: '100%', padding: '14px', fontSize: '15px', fontWeight: 700, background: 'linear-gradient(135deg, #e11d48, #be123c)', borderColor: '#e11d48' }} onClick={handleActivate} disabled={checking}>
                {checking ? 'Đang xử lý...' : status.warranty ? '🔗 Xác nhận liên kết thiết bị vào tài khoản' : status.device?.warrantyMonths === 0 ? '🔗 Kích hoạt & Nhận thiết bị ngay' : '🛡️ Kích hoạt bảo hành & Nhận thiết bị ngay'}
              </button>
            </>
          )}
        </div>
      )}
    </motion.div>
  );
}

// ─── Device Card ───────────────────────────────────────────────────────────────
function DeviceCard({ device, user, onFlash, onRefresh, showToast }: { device: any; user: any; onFlash: (deviceId: string, deviceUid: string) => void, onRefresh: () => void, showToast: (msg: string, type: 'info' | 'error' | 'warning') => void }) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showUnlink, setShowUnlink] = useState(false);
  const [showWarranty, setShowWarranty] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [profile, setProfile] = useState<any>(null);

  const [transferEmail, setTransferEmail] = useState('');

  // Auto-fill user info
  const [warrantyForm, setWarrantyForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    issue: ''
  });

  const [busy, setBusy] = useState(false);

  const warranty = device.device.warrantyRecords?.[0];
  const isNoWarranty = warranty && new Date(warranty.expiresAt).getTime() === new Date(warranty.activatedAt).getTime();
  const warrantyValid = warranty && !isNoWarranty && new Date(warranty.expiresAt) > new Date();
  const daysLeft = warranty && !isNoWarranty ? Math.ceil((new Date(warranty.expiresAt).getTime() - Date.now()) / 86400000) : 0;

  const activeTicket = device.device.tickets?.[0];

  async function handleUnlink() {
    setBusy(true);
    try {
      const res = await apiRequest<{ message: string }>(`/device/${device.device.id}/unlink`, { method: 'POST' }, true);
      showToast(res.message, 'info');
      setShowUnlink(false);
      onRefresh();
    } catch (e: any) {
      showToast(e.message, 'error');
    }
    setBusy(false);
  }

  async function handleWarrantyClaim(e: React.FormEvent) {
    e.preventDefault();
    if (!warrantyForm.name || !warrantyForm.phone || !warrantyForm.address || !warrantyForm.issue) {
      return showToast('Vui lòng điền đầy đủ thông tin (Họ tên, SĐT, Địa chỉ, Mô tả lỗi)', 'error');
    }
    setBusy(true);
    try {
      // 1. Update user profile if it's missing
      if (!profile?.name || !profile?.phone || !profile?.address) {
        await apiRequest('/auth/me', {
          method: 'PUT',
          body: JSON.stringify({ name: warrantyForm.name, phone: warrantyForm.phone, address: warrantyForm.address })
        }, true).catch(err => console.warn('Failed to update profile', err));
      }

      // 2. Submit warranty claim
      const payload = {
        name: warrantyForm.name,
        phone: warrantyForm.phone,
        address: warrantyForm.address,
        issue: warrantyForm.issue
      };

      const res = await apiRequest<{ message: string }>(`/device/${device.device.id}/warranty-claim`, {
        method: 'POST',
        body: JSON.stringify(payload)
      }, true);
      showToast(res.message, 'info');
      setShowWarranty(false);
      setWarrantyForm({ ...warrantyForm, issue: '' });
      onRefresh();
    } catch (e: any) {
      showToast(e.message, 'error');
    }
    setBusy(false);
  }

  async function loadHistory() {
    setHistoryLoading(true);
    setShowHistory(true);
    try {
      const data = await apiRequest<any>(`/device/${device.device.id}/history`, undefined, true);
      setHistoryData(data);
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setHistoryLoading(false);
    }
  }

  async function handleOpenWarranty() {
    setShowMenu(false);
    setShowWarranty(true);
    try {
      const res = await apiRequest<{ user: any }>('/auth/me', undefined, true);
      setProfile(res.user);
      setWarrantyForm(prev => ({
        ...prev,
        name: res.user.name || prev.name,
        phone: res.user.phone || prev.phone,
        address: res.user.address || prev.address,
      }));
    } catch (e) {
      console.warn(e);
    }
  }

  async function handleTransfer() {
    if (!transferEmail) return showToast('Vui lòng nhập email', 'error');
    setBusy(true);
    try {
      const res = await apiRequest<{ message: string }>(`/device/${device.device.id}/transfer`, {
        method: 'POST', body: JSON.stringify({ toEmail: transferEmail })
      }, true);
      showToast(res.message, 'info');
      setShowTransfer(false);
      onRefresh();
    } catch (e: any) {
      showToast(e.message, 'error');
    }
    setBusy(false);
  }

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(5, 7, 12, 0.8)', backdropFilter: 'blur(8px)', zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  };
  const modalContentStyle: React.CSSProperties = {
    background: '#111520', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '24px', padding: '32px',
    width: '90%', maxWidth: '400px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)', color: '#fff'
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '14px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.12)', marginBottom: '16px', fontSize: '15px', outline: 'none', background: 'rgba(255,255,255,0.04)', color: '#fff', transition: 'border-color 0.2s, box-shadow 0.2s', boxSizing: 'border-box'
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 25 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.15 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        style={{
          position: 'relative',
          background: 'linear-gradient(145deg, rgba(20,24,35,0.85) 0%, rgba(13,16,23,0.95) 100%)',
          border: '1px solid var(--glass-border)',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 10px 30px -5px rgba(0,0,0,0.4)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'visible',
          zIndex: showMenu ? 50 : 1
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-6px)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 20px 40px -10px rgba(0,0,0,0.6)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 10px 30px -5px rgba(0,0,0,0.4)'; }}
      >
        {/* Glow effect safely masked inside card border */}
        <div style={{ position: 'absolute', inset: 0, borderRadius: '24px', overflow: 'hidden', pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '150px', height: '150px', background: warrantyValid ? 'radial-gradient(circle, rgba(74,222,128,0.15) 0%, rgba(255,255,255,0) 70%)' : 'radial-gradient(circle, rgba(225,29,72,0.15) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', position: 'relative', zIndex: 10 }}>
          <div>
            <div style={{ fontSize: '22px', fontWeight: 900, color: '#ffffff', fontFamily: '"JetBrains Mono", monospace', letterSpacing: '1px' }}>{device.device.deviceUid}</div>
            <div style={{ fontSize: '15px', color: '#94a3b8', marginTop: '6px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span>{device.device.model}</span>
              {device.device.variant && <span style={{ padding: '2px 8px', background: 'rgba(255,255,255,0.08)', borderRadius: '6px', fontSize: '12px', fontWeight: 600, color: '#cbd5e1' }}>{device.device.variant}</span>}
              <span style={{
                padding: '2px 8px',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: 700,
                background: (device.device.source === 'EXTERNAL_APPROVED' || device.source === 'external_approved') ? 'rgba(56, 189, 248, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                color: (device.device.source === 'EXTERNAL_APPROVED' || device.source === 'external_approved') ? '#38bdf8' : '#4ade80',
                border: `1px solid ${(device.device.source === 'EXTERNAL_APPROVED' || device.source === 'external_approved') ? 'rgba(56, 189, 248, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`
              }}>
                {(device.device.source === 'EXTERNAL_APPROVED' || device.source === 'external_approved') ? '✅ Ngoài Shop' : '🏪 Shop 3D Smart'}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ position: 'relative' }} ref={menuRef}>
              <button style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', fontSize: '20px', cursor: 'pointer', padding: '6px 10px', color: '#cbd5e1', transition: 'all 0.2s', position: 'relative', zIndex: 11 }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                onClick={() => setShowMenu(!showMenu)}>⋮</button>
              {showMenu && (
                <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: '8px', background: '#151923', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '16px', boxShadow: '0 15px 35px -5px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.2)', padding: '8px', zIndex: 100, minWidth: '190px' }}>
                  <button style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: '#e2e8f0', borderRadius: '8px', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    onClick={() => { setShowMenu(false); loadHistory(); }}>
                    🕰️ Lịch sử thiết bị
                  </button>
                  {warrantyValid && !activeTicket && (
                    <>
                      <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '4px 0' }} />
                      <button style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: '#e2e8f0', borderRadius: '8px', transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        onClick={handleOpenWarranty}>
                        🛡️ Gửi bảo hành
                      </button>
                    </>
                  )}
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '4px 0' }} />
                  <button style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: '#e2e8f0', borderRadius: '8px', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    onClick={() => { setShowMenu(false); setShowTransfer(true); }}>
                    🔄 Chuyển nhượng
                  </button>
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '4px 0' }} />
                  <button style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: '#f87171', borderRadius: '8px', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'} onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    onClick={() => { setShowMenu(false); setShowUnlink(true); }}>
                    🗑️ Hủy liên kết
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Chưa kích hoạt & chờ tự động kích hoạt */}
        {!warranty && device.daysUntilAutoActivate !== undefined && device.daysUntilAutoActivate !== null && device.daysUntilAutoActivate > 0 && (
          <div style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '16px', padding: '14px 18px', marginBottom: '24px', fontSize: '13px', color: '#fcd34d', display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', zIndex: 1 }}>
            <span style={{ fontSize: '20px' }}>⏳</span>
            <div>
              <div style={{ fontWeight: 700, color: '#fbbf24' }}>Chưa kích hoạt bảo hành</div>
              <div style={{ color: '#fcd34d', marginTop: '2px' }}>Hệ thống sẽ <strong>tự động kích hoạt sau {device.daysUntilAutoActivate} ngày nữa</strong> (tính từ ngày thêm thiết bị).</div>
            </div>
          </div>
        )}

        {/* Không áp dụng bảo hành */}
        {!warranty && (!device.daysUntilAutoActivate || device.daysUntilAutoActivate <= 0) && (
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.03)', 
            border: '1px solid rgba(255, 255, 255, 0.08)', 
            borderRadius: '16px', 
            padding: '16px 18px', 
            marginBottom: '24px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: '12px',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '38px', 
                height: '38px', 
                borderRadius: '10px', 
                background: 'rgba(255, 255, 255, 0.06)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '18px',
                flexShrink: 0
              }}>
                🛡️
              </div>
              <div>
                <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: '14px' }}>
                  Không áp dụng bảo hành
                </div>
                <div style={{ color: '#94a3b8', fontSize: '12.5px', marginTop: '2px' }}>
                  Hỗ trợ kỹ thuật trọn đời & nạp firmware không giới hạn
                </div>
              </div>
            </div>
            <span style={{ 
              fontSize: '11px', 
              fontWeight: 700, 
              padding: '4px 10px', 
              borderRadius: '6px', 
              background: 'rgba(255, 255, 255, 0.06)', 
              color: '#94a3b8', 
              border: '1px solid rgba(255, 255, 255, 0.1)',
              letterSpacing: '0.5px'
            }}>
              TIÊU CHUẨN
            </span>
          </div>
        )}

        {warranty && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px', position: 'relative', zIndex: 1 }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Kích hoạt</span>
                <span style={{
                  fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '6px',
                  background: warranty.activationReason === 'AUTO_10_DAYS' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                  color: warranty.activationReason === 'AUTO_10_DAYS' ? '#fbbf24' : '#4ade80'
                }}>
                  {warranty.activationReason === 'AUTO_10_DAYS' ? '🤖 Tự động (XK + 3 ngày)' : '👤 Thủ công'}
                </span>
              </div>
              <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '15px' }}>{formatVnDate(warranty.activatedAt)}</div>
              {warranty.activationNote && (
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px', fontStyle: 'italic' }}>
                  {warranty.activationNote}
                </div>
              )}
            </div>
            <div style={{ background: isNoWarranty ? 'rgba(255, 255, 255, 0.03)' : warrantyValid ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)', backdropFilter: 'blur(4px)', border: `1px solid ${isNoWarranty ? 'rgba(255,255,255,0.08)' : warrantyValid ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`, borderRadius: '16px', padding: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: isNoWarranty ? '#94a3b8' : warrantyValid ? '#4ade80' : '#f87171', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
                {isNoWarranty ? 'Hỗ trợ' : 'Ngày hết hạn'}
              </div>
              <div style={{ fontWeight: 700, color: isNoWarranty ? '#cbd5e1' : warrantyValid ? '#4ade80' : '#f87171', fontSize: '15px' }}>
                {isNoWarranty ? 'Hỗ trợ kỹ thuật' : formatVnDate(warranty.expiresAt)}
              </div>
              {warrantyValid && <div style={{ marginTop: '4px', fontSize: '12px', fontWeight: 600, color: '#4ade80' }}>Còn lại {daysLeft} ngày</div>}
            </div>
          </div>
        )}

        {activeTicket && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '12px', padding: '12px 16px', marginBottom: '24px', position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '32px', height: '32px', background: '#f59e0b', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '16px' }}>🛡️</div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#fbbf24' }}>Thiết bị đang được bảo hành</div>
                <div style={{ fontSize: '12px', color: '#fcd34d', marginTop: '2px' }}>Trạng thái: {activeTicket.status === 'open' ? 'Chờ xử lý' : 'Đang xử lý'}</div>
              </div>
            </div>
          </div>
        )}

        <button
          className="button primary"
          style={{ width: '100%', fontWeight: 700, fontSize: '16px', padding: '14px', borderRadius: '14px', background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)', border: 'none', boxShadow: '0 4px 15px rgba(225, 29, 72, 0.35)', color: '#fff', transition: 'transform 0.1s, box-shadow 0.1s', position: 'relative', zIndex: 1 }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(225, 29, 72, 0.5)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(225, 29, 72, 0.35)'; }}
          onClick={() => onFlash(device.device.id, device.device.deviceUid)}
        >
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
            Nạp Firmware
          </span>
        </button>
      </motion.div>

      {/* History Modal */}
      {showHistory && (
        <DeviceHistoryModal
          deviceId={device.device.id}
          initialData={historyData}
          onClose={() => { setShowHistory(false); setHistoryData(null); }}
          showToast={showToast}
        />
      )}

      {/* Transfer Modal */}
      {showTransfer && (
        <div style={modalOverlayStyle} onClick={() => setShowTransfer(false)}>
          <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ width: '48px', height: '48px', background: 'rgba(99, 102, 241, 0.15)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#818cf8', fontSize: '24px' }}>🔄</div>
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: 800, color: '#fff' }}>Chuyển nhượng</h3>
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px' }}>BMCU {device.device.deviceUid}</p>
              </div>
            </div>
            <p style={{ margin: '0 0 20px', color: '#cbd5e1', fontSize: '14px', lineHeight: 1.5, textAlign: 'justify' }}>
              Chuyển quyền sở hữu thiết bị này cho người khác. Họ cần có tài khoản trên hệ thống.
            </p>
            <input type="email" placeholder="Email người nhận..." value={transferEmail} onChange={e => setTransferEmail(e.target.value)} style={inputStyle} />
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button className="button outline" style={{ flex: 1, padding: '12px', borderRadius: '12px' }} onClick={() => setShowTransfer(false)}>Hủy</button>
              <button className="button primary" style={{ flex: 1, padding: '12px', borderRadius: '12px', background: '#6366f1', borderColor: '#6366f1' }} onClick={handleTransfer} disabled={busy}>{busy ? 'Đang xử lý...' : 'Chuyển nhượng'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Unlink Modal */}
      {showUnlink && (
        <div style={modalOverlayStyle} onClick={() => setShowUnlink(false)}>
          <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ width: '48px', height: '48px', background: 'rgba(239, 68, 68, 0.15)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171', fontSize: '24px' }}>🗑️</div>
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: 800, color: '#f87171' }}>Hủy liên kết</h3>
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px' }}>BMCU {device.device.deviceUid}</p>
              </div>
            </div>
            <p style={{ margin: '0 0 24px', color: '#cbd5e1', fontSize: '15px', lineHeight: 1.6, textAlign: 'justify' }}>
              Bạn có chắc chắn muốn hủy liên kết thiết bị này? Bạn sẽ không thể tiếp tục nạp firmware hoặc gửi bảo hành. Hành động này <strong>không thể hoàn tác</strong>.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="button outline" style={{ flex: 1, padding: '12px', borderRadius: '12px' }} onClick={() => setShowUnlink(false)}>Giữ lại</button>
              <button className="button primary" style={{ flex: 1, padding: '12px', borderRadius: '12px', background: '#e11d48', borderColor: '#e11d48' }} onClick={handleUnlink} disabled={busy}>{busy ? 'Đang xử lý...' : 'Đồng ý Hủy'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Warranty Modal */}
      {showWarranty && (
        <div style={modalOverlayStyle} onClick={() => setShowWarranty(false)}>
          <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ width: '48px', height: '48px', background: 'rgba(245, 158, 11, 0.15)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fbbf24', fontSize: '24px' }}>🛡️</div>
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: 800, color: '#fff' }}>Yêu cầu bảo hành</h3>
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px' }}>BMCU {device.device.deviceUid}</p>
              </div>
            </div>

            {(!profile?.name || !profile?.phone || !profile?.address) && (
              <div style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.25)', borderRadius: '12px', padding: '12px', marginBottom: '20px', fontSize: '13px', color: '#93c5fd', lineHeight: 1.5, textAlign: 'justify' }}>
                <strong>Lưu ý:</strong> Vui lòng bổ sung các thông tin còn thiếu. Hệ thống sẽ tự động cập nhật vào hồ sơ của bạn cho những lần sau.
              </div>
            )}

            <form onSubmit={handleWarrantyClaim}>
              {profile?.name ? (
                <div style={{ marginBottom: '16px', background: 'rgba(255,255,255,0.03)', padding: '12px 16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>Họ và tên</div>
                  <div style={{ fontSize: '15px', color: '#fff', fontWeight: 600 }}>{profile.name}</div>
                </div>
              ) : (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#cbd5e1', marginBottom: '6px' }}>Họ và tên <span style={{ color: '#f87171' }}>*</span></label>
                  <input required type="text" placeholder="Nguyễn Văn A" value={warrantyForm.name} onChange={e => setWarrantyForm({ ...warrantyForm, name: e.target.value })} style={inputStyle} />
                </div>
              )}

              {profile?.phone ? (
                <div style={{ marginBottom: '16px', background: 'rgba(255,255,255,0.03)', padding: '12px 16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>Số điện thoại</div>
                  <div style={{ fontSize: '15px', color: '#fff', fontWeight: 600 }}>{profile.phone}</div>
                </div>
              ) : (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#cbd5e1', marginBottom: '6px' }}>Số điện thoại <span style={{ color: '#f87171' }}>*</span></label>
                  <input required type="tel" placeholder="0901234567" value={warrantyForm.phone} onChange={e => setWarrantyForm({ ...warrantyForm, phone: e.target.value })} style={inputStyle} />
                </div>
              )}

              {profile?.address ? (
                <div style={{ marginBottom: '20px', background: 'rgba(255,255,255,0.03)', padding: '12px 16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>Địa chỉ nhận trả bảo hành</div>
                  <div style={{ fontSize: '15px', color: '#fff', fontWeight: 600 }}>{profile.address}</div>
                </div>
              ) : (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#cbd5e1', marginBottom: '6px' }}>Địa chỉ nhận trả bảo hành <span style={{ color: '#f87171' }}>*</span></label>
                  <input required type="text" placeholder="Số nhà, đường, quận/huyện, tỉnh/thành..." value={warrantyForm.address} onChange={e => setWarrantyForm({ ...warrantyForm, address: e.target.value })} style={inputStyle} />
                </div>
              )}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#cbd5e1', marginBottom: '6px' }}>Mô tả lỗi chi tiết <span style={{ color: '#f87171' }}>*</span></label>
                <textarea required placeholder="Thiết bị gặp vấn đề gì? Hãy mô tả rõ để kỹ thuật viên hỗ trợ tốt nhất." value={warrantyForm.issue} onChange={e => setWarrantyForm({ ...warrantyForm, issue: e.target.value })} style={{ ...inputStyle, height: '100px', resize: 'none', marginBottom: 0 }} />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" className="button outline" style={{ flex: 1, padding: '14px', borderRadius: '12px' }} onClick={() => setShowWarranty(false)}>Hủy</button>
                <button type="submit" className="button primary" style={{ flex: 1, padding: '14px', borderRadius: '12px' }} disabled={busy}>{busy ? 'Đang gửi...' : 'Gửi yêu cầu'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Intro Panel ───────────────────────────────────────────────────────────────
function IntroPanel() {
  const user = getAuthUser();
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount: 0.15 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      style={{ padding: '60px 24px', background: 'linear-gradient(180deg, rgba(20,24,35,0.7) 0%, rgba(10,12,18,0.85) 100%)', borderRadius: '24px', textAlign: 'center', border: '1px solid var(--glass-border)', backdropFilter: 'blur(16px)', boxShadow: '0 20px 40px -15px rgba(0,0,0,0.5)' }}
    >
      <h2 style={{ margin: '0 0 16px', fontSize: '36px', color: '#fff', fontWeight: 800, letterSpacing: '-0.5px' }}>Trung tâm quản lý BMCU</h2>
      <p style={{ margin: '0 auto 48px', maxWidth: '640px', color: '#94a3b8', fontSize: '16px', lineHeight: 1.6 }}>
        Nền tảng thông minh dành riêng cho hệ thống 3DSmart BMCU. Kích hoạt bảo hành điện tử, quản lý thiết bị và tự động cập nhật firmware trực tiếp từ trình duyệt web.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '48px', textAlign: 'left' }}>
        <motion.div
          whileHover={{ y: -6, transition: { duration: 0.2 } }}
          style={{ background: 'rgba(255,255,255,0.03)', padding: '32px', borderRadius: '20px', border: '1px solid var(--glass-border)', backdropFilter: 'blur(10px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }}
        >
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>⚡</div>
          <h3 style={{ margin: '0 0 12px', fontSize: '18px', color: '#fff', fontWeight: 700 }}>Web Flashing 1-Click</h3>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', lineHeight: 1.6 }}>Nạp firmware trực tiếp từ trình duyệt qua cổng USB Type-C. Không cần tải công cụ bên ngoài, không cài đặt driver phức tạp.</p>
        </motion.div>
        <motion.div
          whileHover={{ y: -6, transition: { duration: 0.2 } }}
          style={{ background: 'rgba(255,255,255,0.03)', padding: '32px', borderRadius: '20px', border: '1px solid var(--glass-border)', backdropFilter: 'blur(10px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }}
        >
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🛡️</div>
          <h3 style={{ margin: '0 0 12px', fontSize: '18px', color: '#fff', fontWeight: 700 }}>Bảo hành điện tử</h3>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', lineHeight: 1.6 }}>Hệ thống tự động kích hoạt bảo hành dựa trên mã UID độc nhất của vi điều khiển. Theo dõi lịch sử thiết bị dễ dàng.</p>
        </motion.div>
        <motion.div
          whileHover={{ y: -6, transition: { duration: 0.2 } }}
          style={{ background: 'rgba(255,255,255,0.03)', padding: '32px', borderRadius: '20px', border: '1px solid var(--glass-border)', backdropFilter: 'blur(10px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }}
        >
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🧩</div>
          <h3 style={{ margin: '0 0 12px', fontSize: '18px', color: '#fff', fontWeight: 700 }}>Tự động cấu hình</h3>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', lineHeight: 1.6 }}>Hệ thống thông minh tự động tìm kiếm và gợi ý bản firmware phù hợp nhất với model máy in và hệ thống AMS của bạn.</p>
        </motion.div>
      </div>

      {!user ? (
        <a href="/login" className="button primary" style={{ padding: '16px 40px', fontSize: '16px', fontWeight: 600, textDecoration: 'none', display: 'inline-block' }}>
          Đăng nhập để bắt đầu
        </a>
      ) : (
        <p style={{ color: '#94a3b8', margin: 0 }}>Bạn đã đăng nhập. Hãy chọn tab <strong style={{ color: '#fff' }}>Thiết bị của tôi</strong> để bắt đầu quản lý.</p>
      )}
    </motion.div>
  );
}

// ─── Firmware Library Panel ───────────────────────────────────────────────────
function FirmwareLibraryPanel({ onFlashRequest }: { onFlashRequest: () => void }) {
  const [firmwares, setFirmwares] = useState<FirmwareOption[]>(cachedFirmwares || []);
  const [loading, setLoading] = useState(!cachedFirmwares);

  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<string>('all');
  const [filterSlot, setFilterSlot] = useState<string>('all');
  const [filterRetract, setFilterRetract] = useState<string>('all');
  const [page, setPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    async function load() {
      try {
        const res = await apiRequest<{ firmware: FirmwareOption[] }>('/firmware/list');
        cachedFirmwares = res.firmware;
        setFirmwares(res.firmware);
      } catch (e) {
        console.error('Lỗi tải firmware library:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Danh sách các cự ly rút sợi duy nhất
  const retractOptions = useMemo(() => {
    const set = new Set<string>();
    firmwares.forEach(fw => {
      const r = (fw.retractLen || '').replace('m', '').trim();
      if (r) set.add(r);
    });
    return Array.from(set).sort((a, b) => parseFloat(a) - parseFloat(b));
  }, [firmwares]);

  // Bộ lọc dữ liệu
  const filtered = useMemo(() => {
    return firmwares.filter(fw => {
      // 1. Chế độ nạp / Dòng máy
      if (filterMode !== 'all') {
        const pLower = fw.printerModel.toLowerCase();
        if (filterMode === 'standard' && !pLower.includes('standard')) return false;
        if (filterMode === 'soft' && !pLower.includes('soft')) return false;
        if (filterMode === 'highforce' && !pLower.includes('p1s') && !pLower.includes('high')) return false;
      }
      // 2. Vị trí AMS
      if (filterSlot !== 'all') {
        const slot = (fw.amsSlot || fw.variant || '').toUpperCase();
        if (slot !== filterSlot.toUpperCase()) return false;
      }
      // 3. Độ lùi sợi
      if (filterRetract !== 'all') {
        const rClean = (fw.retractLen || '').replace('m', '').trim();
        if (rClean !== filterRetract) return false;
      }
      // 4. Tìm kiếm từ khóa
      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const matchVer = fw.version.toLowerCase().includes(q);
        const matchModel = fw.bmcuModel.toLowerCase().includes(q);
        const matchPrinter = fw.printerModel.toLowerCase().includes(q);
        const matchSlot = (fw.amsSlot || fw.variant || '').toLowerCase().includes(q);
        const matchRetract = (fw.retractLen || '').toLowerCase().includes(q);
        const matchNote = (fw.releaseNote || '').toLowerCase().includes(q);
        if (!matchVer && !matchModel && !matchPrinter && !matchSlot && !matchRetract && !matchNote) return false;
      }
      return true;
    });
  }, [firmwares, filterMode, filterSlot, filterRetract, search]);

  // Reset trang khi đổi bộ lọc
  useEffect(() => {
    setPage(1);
  }, [filterMode, filterSlot, filterRetract, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedFirmwares = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const hasFilters = filterMode !== 'all' || filterSlot !== 'all' || filterRetract !== 'all' || search.trim() !== '';

  const clearFilters = () => {
    setFilterMode('all');
    setFilterSlot('all');
    setFilterRetract('all');
    setSearch('');
  };

  const pillStyle = (active: boolean): React.CSSProperties => ({
    padding: '7px 14px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: active ? 700 : 500,
    cursor: 'pointer',
    border: active ? '1px solid #e11d48' : '1px solid rgba(255,255,255,0.1)',
    background: active ? 'rgba(225, 29, 72, 0.2)' : 'rgba(255,255,255,0.04)',
    color: active ? '#fff' : '#cbd5e1',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
  });

  return (
    <div style={{ color: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: '0 0 8px', fontSize: '1.4rem', color: '#fff', fontWeight: 800 }}>
            📂 Thư viện Firmware BMCU
          </h3>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px' }}>
            Tra cứu và nạp các bản firmware chính thức được tối ưu riêng cho bo mạch BMCU370 Type-C.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: '#94a3b8', background: 'rgba(255,255,255,0.06)', padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
            Tổng số: <strong style={{ color: '#38bdf8' }}>{filtered.length}</strong> / {firmwares.length} bản
          </span>
        </div>
      </div>

      {/* Thanh Bộ Lọc & Tìm Kiếm */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px',
        padding: '18px 20px',
        marginBottom: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px'
      }}>
        {/* Tìm kiếm và Dropdown Độ lùi */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 240px', position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 Tìm theo phiên bản, slot, độ lùi..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '10px',
                background: 'rgba(0,0,0,0.35)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: '#fff',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '14px'
                }}
              >✕</button>
            )}
          </div>

          <div style={{ flex: '0 0 auto', minWidth: '180px' }}>
            <select
              value={filterRetract}
              onChange={e => setFilterRetract(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '10px',
                background: '#0f172a',
                border: '1px solid rgba(255,255,255,0.12)',
                color: '#fff',
                fontSize: '13px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="all">Tất cả cự ly rút sợi</option>
              {retractOptions.map(r => (
                <option key={r} value={r}>Độ lùi {r}m</option>
              ))}
            </select>
          </div>

          {hasFilters && (
            <button
              onClick={clearFilters}
              style={{
                padding: '10px 16px',
                borderRadius: '10px',
                background: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#f87171',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              ✕ Xóa bộ lọc
            </button>
          )}
        </div>

        {/* Lọc theo Dòng máy / Chế độ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', minWidth: '80px' }}>Chế độ nạp:</span>
          <button style={pillStyle(filterMode === 'all')} onClick={() => setFilterMode('all')}>Tất cả</button>
          <button style={pillStyle(filterMode === 'standard')} onClick={() => setFilterMode('standard')}>A1 / A1 Mini (Standard)</button>
          <button style={pillStyle(filterMode === 'soft')} onClick={() => setFilterMode('soft')}>A1 / A1 Mini (Soft Load)</button>
          <button style={pillStyle(filterMode === 'highforce')} onClick={() => setFilterMode('highforce')}>P1S (High Force)</button>
        </div>

        {/* Lọc theo Slot AMS */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', minWidth: '80px' }}>Vị trí Slot:</span>
          <button style={pillStyle(filterSlot === 'all')} onClick={() => setFilterSlot('all')}>Tất cả Slot</button>
          <button style={pillStyle(filterSlot === 'AMS_A')} onClick={() => setFilterSlot('AMS_A')}>AMS_A</button>
          <button style={pillStyle(filterSlot === 'AMS_B')} onClick={() => setFilterSlot('AMS_B')}>AMS_B</button>
          <button style={pillStyle(filterSlot === 'AMS_C')} onClick={() => setFilterSlot('AMS_C')}>AMS_C</button>
          <button style={pillStyle(filterSlot === 'AMS_D')} onClick={() => setFilterSlot('AMS_D')}>AMS_D</button>
        </div>
      </div>

      {/* Danh Sách Card Firmware */}
      {loading ? (
        <CardSkeletonLoader count={3} />
      ) : filtered.length === 0 ? (
        <div style={{
          padding: '48px 24px',
          textAlign: 'center',
          background: 'rgba(255,255,255,0.02)',
          border: '1px dashed rgba(255,255,255,0.1)',
          borderRadius: '16px'
        }}>
          <p style={{ color: '#cbd5e1', fontSize: '16px', margin: '0 0 8px' }}>Không tìm thấy bản firmware nào phù hợp với bộ lọc.</p>
          <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px' }}>Hãy thử điều chỉnh lại chế độ nạp, vị trí slot hoặc từ khóa tìm kiếm.</p>
          <button onClick={clearFilters} className="button secondary" style={{ fontSize: '13px' }}>
            Đặt lại bộ lọc
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {pagedFirmwares.map(fw => {
            const slotName = fw.amsSlot || fw.variant;
            const isA = slotName === 'AMS_A';
            const isB = slotName === 'AMS_B';
            const isC = slotName === 'AMS_C';
            const isD = slotName === 'AMS_D';
            const slotBadgeColor = isA ? '#38bdf8' : isB ? '#4ade80' : isC ? '#f59e0b' : isD ? '#ec4899' : '#a855f7';

            return (
              <div
                key={fw.id}
                style={{
                  background: 'rgba(15, 23, 42, 0.75)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '16px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '14px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                  transition: 'transform 0.15s ease, border-color 0.15s ease',
                }}
              >
                <div>
                  {/* Header Card: Version & Slot */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff' }}>
                        v{fw.version}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        background: 'rgba(255,255,255,0.06)',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#94a3b8'
                      }}>
                        {fw.bmcuModel}
                      </span>
                    </div>

                    {slotName && (
                      <span style={{
                        padding: '3px 10px',
                        background: `${slotBadgeColor}1a`,
                        border: `1px solid ${slotBadgeColor}50`,
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: slotBadgeColor,
                        textTransform: 'uppercase'
                      }}>
                        {slotName}
                      </span>
                    )}
                  </div>

                  {/* Chi tiết Chế độ & Retract */}
                  <div style={{ fontSize: '13px', color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ color: '#64748b' }}>Chế độ:</span>
                      <strong style={{ color: '#f1f5f9' }}>{fw.printerModel}</strong>
                    </div>

                    {fw.retractLen && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#64748b' }}>Độ lùi sợi:</span>
                        <span style={{
                          color: '#38bdf8',
                          background: 'rgba(56, 189, 248, 0.1)',
                          padding: '1px 7px',
                          borderRadius: '5px',
                          fontWeight: 700,
                          fontSize: '12px'
                        }}>
                          {fw.retractLen}m
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Release Note nếu có */}
                  {fw.releaseNote && (
                    <div style={{
                      marginTop: '10px',
                      padding: '8px 10px',
                      background: 'rgba(255,255,255,0.02)',
                      borderLeft: '2px solid #e11d48',
                      borderRadius: '0 6px 6px 0',
                      fontSize: '12px',
                      color: '#94a3b8',
                      lineHeight: 1.4
                    }}>
                      {fw.releaseNote}
                    </div>
                  )}
                </div>

                {/* Footer Card: Flash Button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  {fw.fileUrl && (
                    <button
                      onClick={onFlashRequest}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #e11d48, #be123c)',
                        border: 'none',
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(225, 29, 72, 0.3)',
                        transition: 'opacity 0.15s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <span>⚡ Nạp Firmware</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Phân Trang (Pagination) */}
      {totalPages > 1 && (
        <div style={{
          marginTop: '32px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: currentPage === 1 ? '#64748b' : '#fff',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 600
            }}
          >
            ← Trước
          </button>

          <span style={{ fontSize: '13px', color: '#cbd5e1', padding: '0 8px' }}>
            Trang <strong style={{ color: '#fff' }}>{currentPage}</strong> / {totalPages}
          </span>

          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: currentPage === totalPages ? '#64748b' : '#fff',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 600
            }}
          >
            Sau →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function BmcuCenterPage() {
  const user = getAuthUser();
  const [searchParams] = useSearchParams();

  const [tab, setTab] = useState<'intro' | 'firmwares' | 'devices' | 'activate'>(
    (searchParams.get('tab') as any) || 'intro'
  );
  const [devices, setDevices] = useState<any[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [flashTarget, setFlashTarget] = useState<{ deviceId: string; deviceUid: string } | null>(null);
  const flashFwId = searchParams.get('flashFwId');

  async function loadDevices() {
    if (!user) return;
    setLoadingDevices(true);
    const res = await tryApiRequest<{ devices: any[] }>('/device/my-devices', undefined, true);
    setDevices(res?.devices ?? []);
    setLoadingDevices(false);
  }

  useEffect(() => { loadDevices(); }, []);

  function openFlash(deviceId: string, deviceUid: string) {
    setFlashTarget({ deviceId, deviceUid });
  }

  // Force intro/firmware tab if not logged in
  useEffect(() => {
    if (!user && tab !== 'intro' && tab !== 'firmwares') setTab('intro');
  }, [user, tab]);

  const TABS = user
    ? [['intro', '🌟 Giới thiệu'], ['firmwares', '📂 Thư viện Firmware'], ['devices', '📋 Thiết bị của tôi'], ['activate', '➕ Kích hoạt BMCU mới']] as const
    : [['intro', '🌟 Giới thiệu'], ['firmwares', '📂 Thư viện Firmware']] as const;

  return (
    <div className="page-block" style={{ position: 'relative' }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{ marginBottom: '32px' }}
      >
        <EditablePageHeader pageKey="bmcu" defaultEyebrow="BMCU Center" defaultTitle="Trung tâm BMCU" />
      </motion.div>

      {/* Tabs with synchronized smooth upward entrance */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
        style={{ display: 'flex', gap: '8px', marginBottom: '32px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0', position: 'relative' }}
      >
        {TABS.map(([t, label]) => {
          const isActive = tab === t;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                position: 'relative',
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontWeight: isActive ? 700 : 500,
                fontSize: '15px',
                color: isActive ? '#fff' : '#94a3b8',
                marginBottom: '-1px',
                transition: 'color 0.2s ease',
              }}
            >
              {label}
              {isActive && (
                <motion.div
                  layoutId="bmcu-tab-indicator"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '2.5px',
                    background: 'linear-gradient(90deg, #f43f5e, #e11d48)',
                    borderRadius: '3px 3px 0 0',
                    boxShadow: '0 0 12px rgba(225, 29, 72, 0.8)',
                  }}
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                />
              )}
            </button>
          );
        })}
      </motion.div>

      {/* Tab Panels with Smooth Transition */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          style={{ minHeight: '560px' }}
        >
          {/* Intro Tab */}
          {tab === 'intro' && <IntroPanel />}

          {/* Firmwares Tab */}
          {tab === 'firmwares' && (
            <FirmwareLibraryPanel onFlashRequest={() => {
              if (!user) {
                showToast('Vui lòng đăng nhập để nạp Firmware cho thiết bị của bạn!', 'error');
                setTimeout(() => { window.location.href = '/login'; }, 1500);
              } else {
                setTab('devices');
                setTimeout(() => {
                  if (devices.length === 0) {
                    showToast('Bạn chưa có thiết bị nào! Vui lòng chọn "Kích hoạt BMCU mới" để đăng ký.', 'error');
                  } else {
                    showToast('Hãy nhấn nút "Nạp Firmware" trên thiết bị của bạn để nạp.', 'info');
                  }
                }, 100);
              }
            }} />
          )}

          {/* Devices Tab */}
          {tab === 'devices' && (
            <div>
              {loadingDevices ? (
                <CardSkeletonLoader count={2} />
              ) : devices.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5 }}
                  style={{ textAlign: 'center', padding: '60px 24px', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: '24px' }}
                >
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                  <h3 style={{ margin: '0 0 12px', color: '#fff', fontSize: '1.4rem', fontWeight: 800 }}>Bạn chưa có thiết bị nào</h3>
                  <p style={{ margin: '0 0 24px', color: '#94a3b8' }}>Kích hoạt BMCU để bắt đầu quản lý và nạp firmware.</p>
                  <button className="button primary" onClick={() => setTab('activate')}>➕ Kích hoạt BMCU đầu tiên</button>
                </motion.div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
                  {devices.map(d => (
                    <DeviceCard key={d.id} device={d} user={user} onFlash={openFlash} onRefresh={loadDevices} showToast={showToast} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Activate Tab */}
          {tab === 'activate' && (
            <ActivateDevicePanel onActivated={() => {
              showToast('Kích hoạt thành công!', 'info');
              setTab('devices');
              loadDevices();
            }} showToast={showToast} />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Flash Modal */}
      {flashTarget && (
        <FirmwareWizard
          deviceId={flashTarget.deviceId}
          deviceUid={flashTarget.deviceUid}
          onClose={() => { setFlashTarget(null); loadDevices(); }}
        />
      )}
    </div>
  );
}
